import { useState, useEffect } from "react";
import {
  Shield,
  Plus,
  Mail,
  X,
  Loader2,
  Trash2,
  AlertTriangle,
  Crown,
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/Toast";

function InviteAdminModal({ onClose, onSuccess }) {
  const { showSuccess, showError } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;

    setSaving(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("No hay sesión activa");

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-admin`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify(form),
        }
      );

      const data = await response.json();
      if (!response.ok || data.error)
        throw new Error(data.error || "Error al invitar administrador");

      showSuccess(`Invitación enviada a ${form.email}`);
      onSuccess();
      onClose();
    } catch (err) {
      showError(err.message || "Error al invitar administrador");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-indigo-600 to-indigo-700">
          <div>
            <h2 className="text-lg font-bold text-white">
              Invitar Administrador
            </h2>
            <p className="text-sm text-indigo-200">
              Recibirá un email para activar la cuenta
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg text-white"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre completo *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="María López"
              required
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="maria@ejemplo.com"
              required
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            El administrador podrá ver dashboard y alumnos, pero no podrá editar
            actividades ni gestionar otros administradores.
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !form.name.trim() || !form.email.trim()}
              className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2 transition-colors"
            >
              {saving ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  <Mail size={18} />
                  Enviar invitación
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteAdminModal({ admin, onConfirm, onClose, deleting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle size={24} className="text-red-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              Eliminar administrador
            </h3>
            <p className="text-sm text-gray-500">
              Esta acción no se puede deshacer
            </p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-4">
          <p className="font-medium text-gray-900">{admin.name}</p>
          <p className="text-sm text-gray-500">{admin.email}</p>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          El usuario perderá acceso al panel inmediatamente.
        </p>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          >
            {deleting ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <>
                <Trash2 size={18} />
                Eliminar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminsSection() {
  const { user, isSuperAdmin } = useAuth();
  const { showSuccess, showError } = useToast();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchAdmins = async () => {
    const { data } = await supabase
      .from("admins")
      .select("*")
      .order("created_at");
    setAdmins(data || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleDelete = async () => {
    if (!adminToDelete) return;

    setDeleting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("No hay sesión activa");

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-admin`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ admin_id: adminToDelete.id }),
        }
      );

      const data = await response.json();
      if (!response.ok || data.error)
        throw new Error(data.error || "Error al eliminar");

      showSuccess("Administrador eliminado correctamente");
      setAdminToDelete(null);
      fetchAdmins();
    } catch (err) {
      showError(err.message || "Error al eliminar administrador");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse h-32 bg-gray-200 rounded-xl" />;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-indigo-600">
          <Shield size={20} />
          <h2 className="font-semibold text-lg text-gray-900">
            Administradores
          </h2>
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => setShowInvite(true)}
            className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium flex items-center gap-2 transition-colors"
          >
            <Plus size={16} />
            Invitar admin
          </button>
        )}
      </div>
      <p className="text-sm text-gray-500">
        Los administradores tienen acceso al panel de gestión de alumnos. Los
        super administradores además pueden editar actividades e invitar a
        otros administradores.
      </p>

      <div className="space-y-2">
        {admins.length === 0 ? (
          <p className="text-gray-500 py-4 text-center">
            No hay administradores configurados
          </p>
        ) : (
          admins.map((admin) => {
            const isSelf = admin.id === user?.id;
            const isSuper = admin.role === "super_admin";
            return (
              <div
                key={admin.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                    {admin.name}
                    {isSelf && (
                      <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                        Tú
                      </span>
                    )}
                    {isSuper && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded flex items-center gap-1">
                        <Crown size={10} />
                        Super admin
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {admin.email}
                  </p>
                </div>
                {isSuperAdmin && !isSelf && !isSuper && (
                  <button
                    onClick={() => setAdminToDelete(admin)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Eliminar administrador"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {showInvite && (
        <InviteAdminModal
          onClose={() => setShowInvite(false)}
          onSuccess={fetchAdmins}
        />
      )}

      {adminToDelete && (
        <DeleteAdminModal
          admin={adminToDelete}
          onConfirm={handleDelete}
          onClose={() => setAdminToDelete(null)}
          deleting={deleting}
        />
      )}
    </div>
  );
}

function BlocksSection() {
  const blocks = [
    { name: "Tu punto de partida", order: 0 },
    { name: "Polaris", order: 1 },
    { name: "Núcleo", order: 2 },
    { name: "Sistema de Fidelización PRO", order: 3 },
    { name: "Libera tu tiempo", order: 4 },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
      <h2 className="font-semibold text-lg text-gray-900">
        Bloques del Programa
      </h2>
      <p className="text-sm text-gray-500">
        Los bloques organizan las actividades en secciones secuenciales.
      </p>

      <div className="space-y-2">
        {blocks.map((block) => (
          <div
            key={block.name}
            className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
          >
            <span className="w-8 h-8 flex items-center justify-center bg-indigo-100 text-indigo-700 rounded-full font-semibold text-sm">
              {block.order}
            </span>
            <span className="font-medium text-gray-900">{block.name}</span>
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-400 pt-2 border-t border-gray-100">
        Para modificar la estructura de bloques, contacta con el equipo técnico.
      </p>
    </div>
  );
}

export default function AdminConfig() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configuración</h1>
        <p className="text-gray-500">Administración del sistema</p>
      </div>

      <AdminsSection />
      <BlocksSection />
    </div>
  );
}
